/**
 * ADD TO CALENDAR helpers for the event page WHERE tab
 * (docs/plans/2026-09-events-clubs-redesign/handoff/README.md §1 · WHERE).
 *
 * Two targets: a Google Calendar "render" link, and an .ics file (Apple Calendar,
 * Outlook, everything else). Pure — no DOM access except in `downloadIcs`.
 */

function pad(n) { return String(n).padStart(2, '0'); }

/** `20260314T200000Z` — UTC, the format both Google and RFC 5545 accept. */
export function icsStamp(iso) {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

/** Events without an end time get a three-hour block — a calendar needs an end. */
export function resolveEnd(startIso, endIso) {
  if (endIso) return endIso;
  const d = new Date(startIso);
  if (Number.isNaN(d.getTime())) return startIso;
  return new Date(d.getTime() + 3 * 60 * 60 * 1000).toISOString();
}

export function googleCalendarUrl({ title, startUtc, endUtc, location, details }) {
  const p = new URLSearchParams();
  p.set('action', 'TEMPLATE');
  p.set('text', title || 'Party');
  p.set('dates', `${icsStamp(startUtc)}/${icsStamp(resolveEnd(startUtc, endUtc))}`);
  if (location) p.set('location', location);
  if (details) p.set('details', details);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function escapeIcs(s) {
  return String(s || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function buildIcs({ uid, title, startUtc, endUtc, location, details, url }) {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Portal//Event//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid || `${icsStamp(startUtc)}@partyresume.com`}`,
    `DTSTAMP:${icsStamp(new Date())}`,
    `DTSTART:${icsStamp(startUtc)}`,
    `DTEND:${icsStamp(resolveEnd(startUtc, endUtc))}`,
    `SUMMARY:${escapeIcs(title)}`,
  ];
  if (location) lines.push(`LOCATION:${escapeIcs(location)}`);
  if (details) lines.push(`DESCRIPTION:${escapeIcs(details)}`);
  if (url) lines.push(`URL:${url}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

/** Hands the browser an .ics file. Returns false when the DOM is unavailable. */
export function downloadIcs(input) {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return false;
  const blob = new Blob([buildIcs(input)], { type: 'text/calendar;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${(input.title || 'party').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'party'}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  return true;
}

/** Build the calendar payload the WHERE tab uses from an EventDto. */
export function calendarPayloadForEvent(event, pageUrl) {
  if (!event) return null;
  const location = [event.venueName, event.locationRevealed ? event.locationAddress : (event.venueAddress || event.locationCity)]
    .filter(Boolean)
    .join(', ');
  return {
    uid: event.id ? `${event.id}@partyresume.com` : undefined,
    title: event.title,
    startUtc: event.dateTime,
    endUtc: event.endDateTime || null,
    location: location || undefined,
    details: pageUrl ? `Portal — ${pageUrl}` : undefined,
    url: pageUrl,
  };
}
